// ---------------------------------------------------------------------------
// The host's powers over one chat line, opened from the line itself.
//
// **WHY IT IS ON THE MESSAGE AND NOT ONLY IN THE ROSTER.** The roster is a list
// of people and this is a list of what people said, and the second is where a
// host is actually looking when they decide somebody has to go: moderation
// starts with a sentence, not with a name. Going from the sentence to the person
// meant reading the name off the row, switching to People, finding them again
// and opening their menu — four steps, mid-round, on a phone, to act on
// something already under the thumb. The powers themselves are unchanged and
// still work in every phase; this is somewhere to press them from.
//
// It carries four items and they are not the roster's four. `Mute` and
// `Make host` are about a person in the abstract and stay in the roster. Delete
// and purge are about what was said and can only be here. Kick and ban are in
// both, because they are what "somebody has to go" means and this is where a
// host decides it.
//
// **The author may not be in the room any more, and that is the ordinary case
// rather than the edge one.** Isaac's case is somebody kicking an account,
// forgetting, and wanting what they left behind gone afterwards — so purge is
// keyed on the id the messages carry and is offered whether or not anybody still
// holds that seat. Kick and ban need a seat and are hidden without one, because
// the engine refuses both for a player it cannot find and an item whose only
// outcome is an error toast is not an item.
//
// **THE DISMISSAL RULES ARE COPIED FROM `HostRowMenu` AND ARE NOT NEGOTIABLE.**
// A document-level `pointerdown` in the CAPTURE phase, and nothing that also
// closes on a move or a release. The scrim press gets `swallowNextClick`,
// because closing on the press unmounts the backdrop before the finger comes up
// and the browser then hit-tests the click against whatever is underneath — in
// this panel that is another message's own three-dots. CLAUDE.md records both,
// and `lib/swallowNextClick.ts` argues the second out in full.
//
// **The popover is portalled and fixed, which `HostRowMenu`'s is not.** That one
// is anchored `absolute` inside its row, which works in the lobby's seat list;
// here the rows live inside an `overflow-y-auto` scroller, and an absolutely
// positioned child of a clipping box is clipped. So the menu is measured against
// the trigger's client rect and drawn on `document.body`. The cost of a fixed
// box is that it does not follow the list, so a scroll closes it — which is what
// a press inside the list already did, since the press is outside the menu.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactElement, ReactNode } from 'react';
import type { ChatMessage, ClientMessage, PublicPlayer } from '@countdown/shared';
import { BanIcon, Icon, KickIcon, MoreIcon, TrashIcon } from '../primitives/Icon.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import { useVisualViewport } from '../../hooks/useVisualViewport.js';
import { swallowNextClick } from '../../lib/swallowNextClick.js';

/** Breathing room between the menu and the edge of what is on screen. */
const MENU_MARGIN = 8;

/**
 * Below this there is not enough room to open downward at all, so the menu flips
 * above the trigger instead. Four rows and a rule: less than that and a host is
 * scrolling a menu to reach its second item.
 */
const MENU_MIN_HEIGHT = 176;

/** How much of the visible height the phone's sheet may take. */
const SHEET_SHARE = 0.72;

/** Where the menu opened and how tall it may be, all in CSS pixels. */
interface Placement {
  above: boolean;
  top: number;
  right: number;
  maxHeight: number;
}

export interface ChatModerationMenuProps {
  message: ChatMessage;
  /**
   * The author's seat, or null when they have left. Null hides kick and ban and
   * changes nothing else: purge is keyed on `message.authorId`, which outlives
   * the seat, and that is the whole reason this control is on the message.
   */
  author: PublicPlayer | null;
  send: (message: ClientMessage) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChatModerationMenu({
  message,
  author,
  send,
  open,
  onOpenChange,
}: ChatModerationMenuProps): ReactElement {
  // Two confirmations, one at a time. Both are destructive in a way one stray
  // tap next to Delete must not reach: a ban is room-scoped and keyed on a
  // device and an IP, and a purge takes down lines nobody on this screen can
  // see any more.
  const [confirming, setConfirming] = useState<'ban' | 'purge' | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const scrimRef = useRef<HTMLDivElement | null>(null);
  // Outside the close effect on purpose: closing is what tears that effect
  // down, and a timer cleared by its own cause never runs.
  const restoreRef = useRef(0);
  useEffect(() => () => window.clearTimeout(restoreRef.current), []);

  const viewport = useVisualViewport();
  const isDesktop = useIsDesktop();

  const visibleHeight = viewport.height > 0 ? viewport.height : window.innerHeight;
  const name = message.authorName ?? 'someone';

  /**
   * Where this menu fits right now. Popover only — a sheet has no geometry to
   * work out, which is the reason it is a sheet.
   *
   * `bottomInset` rather than `coveredHeight`, because everything here is
   * compared against a client rect. The algebra is written out on `place()` in
   * `HostRowMenu`; the short version is that `innerHeight - bottomInset` is
   * exactly where the bottom of the visible region falls inside the layout
   * viewport, which is the number a `getBoundingClientRect` answer belongs
   * beside.
   */
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (trigger === null) return;
    const rect = trigger.getBoundingClientRect();
    const height = viewport.height > 0 ? viewport.height : window.innerHeight;
    const visibleBottom = window.innerHeight - viewport.bottomInset;
    const bandTop = Math.max(0, visibleBottom - height);

    const below = visibleBottom - rect.bottom - MENU_MARGIN;
    const above = rect.top - bandTop - MENU_MARGIN;
    // Down unless down is unusable. A menu that flipped up whenever up was
    // roomier would jump sides halfway down a list for no reason a host can see.
    const flip = below < MENU_MIN_HEIGHT && above > below;
    setPlacement({
      above: flip,
      top: flip ? Math.max(bandTop, rect.top - MENU_MARGIN) : rect.bottom + 4,
      right: Math.max(MENU_MARGIN, window.innerWidth - rect.right),
      // Never below the minimum: on a screen with room for neither, a menu that
      // scrolls its own contents beats one squeezed to nothing.
      maxHeight: Math.max(MENU_MIN_HEIGHT, Math.round(flip ? above : below)),
    });
  }, [viewport.bottomInset, viewport.height]);

  // One geometry for every row, so the menu is the same size whichever items it
  // is holding. Only the colour differs.
  const row =
    'flex min-h-11 w-full items-center gap-2.5 rounded-chip px-3 text-left text-[13px] font-semibold transition-colors duration-150 ease-out';
  const danger = `${row} text-alert hover:bg-alert/15`;
  const divider = <div aria-hidden="true" className="mx-2 my-1 h-px bg-line" />;

  const close = useCallback(() => {
    onOpenChange(false);
    triggerRef.current?.focus();
  }, [onOpenChange]);

  /** Every item that does not ask a follow-up: send it, and the menu is done. */
  const choose = (outgoing: ClientMessage) => () => {
    send(outgoing);
    close();
  };

  useEffect(() => {
    if (!open) return;

    // `pointerdown`, not `click`: a press that begins outside the menu already
    // means "not this menu", and waiting for the click leaves it open under a
    // finger that has started a scroll. Captured, so an item that stops
    // propagation cannot keep the menu alive.
    //
    // Nothing here may be broadened to `pointermove` or `pointerup`. CLAUDE.md
    // records why — a control inside a menu can be a drag, and a rule that
    // closes on any press ENDING outside dismisses the menu mid-gesture.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) === true) return;
      if (surfaceRef.current?.contains(target) === true) return;
      // A press on the scrim means GET ME OUT and nothing else. See
      // `swallowNextClick`, and note the condition: only when there was a
      // backdrop to press.
      if (scrimRef.current?.contains(target) === true) swallowNextClick();
      onOpenChange(false);
      // A task later, because the browser's own press handling moves focus
      // after this listener runs, and only if nothing else claimed it.
      restoreRef.current = window.setTimeout(() => {
        if (document.activeElement === document.body) triggerRef.current?.focus();
      }, 0);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    // The message list is what scrolls, and a scroll on a container does not
    // bubble to the window — hence the capture. A fixed popover does not follow
    // the row it was opened from, so it closes rather than drifting away from
    // it. The sheet is pinned to the screen and subscribes to neither.
    const onScroll = () => onOpenChange(false);

    if (isDesktop) {
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onScroll);
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close, isDesktop, onOpenChange]);

  /**
   * Every item, once, for both shapes. Two copies of this list is how the phone
   * and the desktop end up offering different powers over the same person.
   *
   * **The ban says what it takes with it.** A ban purges — the room does not
   * want the person or what they said — and a confirmation that only promised to
   * keep them out would be under-reporting what the press does. There is no
   * separate "ban without purging": a host who wants the lines kept has the
   * kick, one item up.
   */
  const items: ReactNode = (
    <>
      <button type="button" onClick={choose({ type: 'delete-chat-message', messageId: message.id })} className={danger}>
        <Icon icon={TrashIcon} size={16} />
        Delete this message
      </button>
      {/* A line with no author at all is a system line, and the panel does not
          open this menu on one. The guard is here anyway because it is what
          makes `authorId` a string rather than a string-or-empty, and an empty
          author id sent to `purge-chat` names everybody's nothing. */}
      {message.authorId === null ? null : confirming === 'purge' ? (
        <button
          type="button"
          // `purge-chat` is keyed on the author id the message carries, never on
          // a seat: the case this exists for is a host who kicked somebody an
          // hour ago and wants what they left behind gone now.
          onClick={choose({ type: 'purge-chat', authorId: message.authorId })}
          className={`${danger} bg-alert/15`}
        >
          <Icon icon={TrashIcon} size={16} />
          Delete every one of them
        </button>
      ) : (
        <button type="button" onClick={() => setConfirming('purge')} className={danger}>
          <Icon icon={TrashIcon} size={16} />
          Delete everything they said
        </button>
      )}
      {author === null ? null : (
        <>
          {divider}
          <button type="button" onClick={choose({ type: 'kick-player', playerId: author.id })} className={danger}>
            <Icon icon={KickIcon} size={16} />
            Kick
          </button>
          {confirming === 'ban' ? (
            <button
              type="button"
              onClick={choose({ type: 'ban-player', playerId: author.id })}
              className={`${danger} bg-alert/15`}
            >
              <Icon icon={BanIcon} size={16} />
              Ban, and delete everything they said
            </button>
          ) : (
            <button type="button" onClick={() => setConfirming('ban')} className={danger}>
              <Icon icon={BanIcon} size={16} />
              Ban
            </button>
          )}
        </>
      )}
    </>
  );

  /** Whose line this is. A sheet is at the other end of the screen from it. */
  const heading = (
    <div className="shrink-0 px-3 pb-1.5">
      <span className="font-mono text-[10px] font-semibold tracking-[0.08em] text-ink-dim uppercase">
        {name}
      </span>
    </div>
  );

  /**
   * The phone's shape.
   *
   * Pinned with `coveredHeight`, not `bottomInset`: this is a `position: fixed`
   * box, and the note on those two readings in `useVisualViewport.ts` says which
   * is which. `bottomInset` carries two terms the browser has already applied to
   * a fixed element by the time it is painted, so a sheet held up by it slides
   * around the screen as the page scrolls under a keyboard.
   */
  const sheet = createPortal(
    <div
      ref={scrimRef}
      data-testid="chat-menu-scrim"
      className="fade-in fixed inset-0 z-[35] flex flex-col justify-end"
      style={{ background: 'var(--c-scrim)', paddingBottom: viewport.coveredHeight }}
    >
      <div
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Moderate ${name}’s message`}
        data-testid="chat-menu"
        data-menu-variant="sheet"
        style={{ maxHeight: Math.max(MENU_MIN_HEIGHT, Math.round(visibleHeight * SHEET_SHARE)) }}
        className="sheet-up mx-auto flex w-full max-w-[420px] flex-col gap-0.5 overflow-y-auto overscroll-contain rounded-t-frame border-t border-line bg-surface p-2 pb-[max(env(safe-area-inset-bottom),10px)]"
      >
        {/* The grab bar says the sheet is dismissible without spending a row on
            a Cancel button. It is not a control: pressing anywhere off the sheet
            is the gesture. */}
        <div className="flex shrink-0 justify-center pt-1 pb-2">
          <span aria-hidden="true" className="h-1 w-9 rounded-chip bg-line-strong" />
        </div>
        {heading}
        {items}
      </div>
    </div>,
    document.body,
  );

  const popover = createPortal(
    <div
      ref={surfaceRef}
      data-testid="chat-menu"
      data-menu-variant="popover"
      data-menu-above={placement?.above === true || undefined}
      style={
        placement === null
          ? { top: 0, right: 0, maxHeight: MENU_MIN_HEIGHT }
          : {
              right: placement.right,
              maxHeight: placement.maxHeight,
              ...(placement.above
                ? { bottom: Math.max(0, window.innerHeight - placement.top) }
                : { top: placement.top }),
            }
      }
      className="fixed z-[35] flex w-60 flex-col gap-0.5 overflow-y-auto overscroll-contain rounded-card border border-line bg-surface p-1.5"
    >
      {heading}
      {items}
    </div>,
    document.body,
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Moderate ${name}’s message`}
        aria-haspopup={isDesktop ? 'true' : 'dialog'}
        aria-expanded={open}
        onClick={() => {
          if (open) {
            close();
            return;
          }
          // Reset on the way in rather than on the way out: a menu that closed
          // mid-confirmation opens back on the first press, so the second one
          // cannot land on a question the host has already walked away from.
          setConfirming(null);
          if (isDesktop) place();
          onOpenChange(true);
        }}
        // 32px rather than the 44 this app holds every other tap target to, and
        // the trade is the one the delete control it replaces already made: 44
        // is the rule for something a player hits under time pressure, and a 44px
        // column would make a chat row nearly twice as tall for the one person
        // moderating it as for everybody reading it. It is what the composer's
        // own send button in this panel is.
        className="grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-chip text-ink-dim transition-colors duration-150 ease-out hover:bg-raised hover:text-ink"
      >
        <Icon icon={MoreIcon} size={13} />
      </button>
      {open ? (isDesktop ? popover : sheet) : null}
    </>
  );
}
